/*
 * Tipos do wire da trilha de auditoria (FE-5 CP-B — ADR 0026).
 *
 * Espelham FIELMENTE os schemas Pydantic do backend (`flow/api/auditoria/`):
 * `EmpresaAuditavelOut`, `ItemAuditoriaResumoOut` e `AuditoriaDetalheOut`.
 *
 * NOTA DE RIGOR — esta camada NAO lida com dinheiro: a trilha de auditoria so
 * carrega metadados (acao, recurso, ts, ids, correlation_id) e os snapshots
 * `antes`/`depois`. Logo NAO ha campo monetario aqui — nenhuma das interfaces
 * abaixo tem (nem deve ter) `string` representando `NUMERIC`. Caso um snapshot
 * `antes`/`depois` venha a conter valor monetario, ele e dado OPACO
 * (`Record<string, unknown>`) ja MASCARADO pelo backend — esta camada nunca o
 * interpreta nem o converte. Mantem-se, ainda assim, o rigor do projeto: tipos
 * estritos, sem `any`, e nenhum dado sensivel persistido no cliente.
 */

/**
 * Empresa visivel ao usuario no escopo da acao `ver_auditoria`
 * (`GET /api/auditoria/empresas`).
 *
 * A lista PODE incluir empresas INATIVAS (forense): `ativa` marca isso — a UI
 * (CP-C) decide como sinalizar. `fantasia` e anulavel. A lista pode vir VAZIA
 * (`[]`) quando o escopo do usuario nao inclui empresa alguma — o tipo ja
 * contempla; o tratamento de "escopo vazio" na UI e do CP-C.
 */
export interface EmpresaAuditavel {
  id: string;
  razao_social: string;
  fantasia: string | null;
  cnpj: string;
  ativa: boolean;
}

/**
 * Item da trilha em formato resumo
 * (`GET /api/empresas/{empresaId}/auditoria`).
 *
 * RESPOSTA E ARRAY PURO — sem envelope/total/paginacao no corpo; a paginacao e
 * por `limit`/`offset` na query (ver `FiltrosTrilha`). `ts` e datetime ISO.
 * `usuario_id`/`correlation_id` sao anulaveis (eventos de sistema/sem
 * correlacao). O resumo NAO traz `antes`/`depois` — esses vem so no detalhe.
 */
export interface ItemAuditoriaResumo {
  id: string;
  ts: string;
  acao: string;
  recurso: string;
  usuario_id: string | null;
  correlation_id: string | null;
}

/**
 * Detalhe de um registro da trilha
 * (`GET /api/empresas/{empresaId}/auditoria/{registroId}`).
 *
 * Estende o resumo com os snapshots `antes`/`depois`, que ja vem MASCARADOS pelo
 * backend (esta camada nunca desmascara). Sao dado OPACO
 * (`Record<string, unknown> | null`) — `null` quando nao ha snapshot daquele
 * lado (ex.: criacao nao tem `antes`, exclusao nao tem `depois`).
 */
export interface AuditoriaDetalhe extends ItemAuditoriaResumo {
  antes: Record<string, unknown> | null;
  depois: Record<string, unknown> | null;
}

/**
 * Filtros da consulta da trilha (query params do `GET .../auditoria`).
 *
 * `limit`/`offset` sao SEMPRE enviados (tem default no backend e nesta camada).
 * Os demais sao OPCIONAIS: a camada de `api` OMITE do objeto `params` toda chave
 * opcional ausente ou vazia (nao envia `acao=""`). `data_de`/`data_ate` sao
 * datetime ISO; `acao` deve ser um valor de `ACOES_AUDITORIA`; `usuario_id` e um
 * UUID string; `recurso_prefixo` e prefixo livre (<= 256).
 */
export interface FiltrosTrilha {
  limit: number;
  offset: number;
  acao?: string;
  usuario_id?: string;
  data_de?: string;
  data_ate?: string;
  recurso_prefixo?: string;
}

/**
 * Acoes canonicas do StrEnum `Acao` do backend (`flow/seguranca/acoes.py`).
 *
 * FONTE UNICA do vocabulario de acao para o filtro da UI (CP-C). Tupla `as const`
 * (readonly) — as 13 strings EXATAS do enum, na ordem do backend. Qualquer drift
 * com o backend deve ser corrigido aqui (e no enum), nunca improvisado na UI.
 */
export const ACOES_AUDITORIA = [
  "visualizar_financeiro",
  "exportar_extrato",
  "criar_receita_manual",
  "conciliar",
  "desfazer_conciliacao",
  "configurar_alertas",
  "testar_canal",
  "gerenciar_usuarios",
  "ver_auditoria",
  "cadastrar_infraestrutura",
  "editar_calendario",
  "visualizar_projecao",
  "semear_saldo",
] as const;

/** Limite padrao de itens por pagina — espelha o default do backend. */
export const LIMITE_DEFAULT = 50;
